import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, Switch, Typography } from '@mui/material';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { apiClient } from '../../services/api';
import { CompanyExportPreferences, ExportPreview } from '../../types';

const defaults: CompanyExportPreferences = {
  productsStockEnabled: false,
  customerStatusEnabled: false,
  accountMovementsEnabled: false,
  salesSummaryEnabled: false,
  detailedSalesEnabled: false,
  maxRows: 50000,
};

const ExportSettings: React.FC = () => {
  const [preferences, setPreferences] = useState(defaults);
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof apiClient.getExportPreferences !== 'function') {
      setLoading(false);
      return;
    }
    void apiClient.getExportPreferences().then(setPreferences).catch(() => setMessage('Dışa aktarma ayarları yüklenemedi.')).finally(() => setLoading(false));
  }, []);

  const update = async (key: keyof Omit<CompanyExportPreferences, 'maxRows'>, checked: boolean) => {
    const next = { ...preferences, [key]: checked };
    setPreferences(next);
    const request = {
      productsStockEnabled: next.productsStockEnabled,
      customerStatusEnabled: next.customerStatusEnabled,
      accountMovementsEnabled: next.accountMovementsEnabled,
      salesSummaryEnabled: next.salesSummaryEnabled,
      detailedSalesEnabled: next.detailedSalesEnabled,
    };
    try { await apiClient.updateExportPreferences(request); setPreview(null); }
    catch { setMessage('Dışa aktarma ayarı kaydedilemedi.'); setPreferences(preferences); }
  };

  const prepareExport = async () => {
    setBusy(true); setMessage(null);
    try {
      const result = await apiClient.getExportPreview();
      setPreview(result);
      if (!result.allowed) { setMessage(result.message); return; }
      const download = await apiClient.exportCompanyData();
      const url = URL.createObjectURL(download.blob);
      const link = document.createElement('a'); link.href = url; link.download = download.fileName; link.click(); URL.revokeObjectURL(url);
      setMessage('Dışa aktarma dosyası hazırlandı.');
    } catch { setMessage('Dışa aktarma gerçekleştirilemedi.'); }
    finally { setBusy(false); }
  };

  const options = [
    ['productsStockEnabled', 'Ürün, stok ve parti bilgileri'],
    ['customerStatusEnabled', 'Müşterilerin güncel durumları'],
    ['accountMovementsEnabled', 'Cari hareketler'],
    ['salesSummaryEnabled', 'Satış özeti'],
    ['detailedSalesEnabled', 'Detaylı satışlar'],
  ] as const;

  return <Card component="section" aria-label="Veri dışa aktarma" data-testid="company-export-settings" sx={{ mt: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={750}>Veri dışa aktarma</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>İhtiyacınız olan veri türlerini seçin. Başlangıçta tüm seçenekler kapalıdır.</Typography>
      <Stack divider={<Divider flexItem />}>
        {options.map(([key, label]) => <Box key={key} sx={{ py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">{label}</Typography>
          <Switch inputProps={{ 'aria-label': label }} checked={preferences[key]} disabled={loading || busy} onChange={(_, checked) => void update(key, checked)} />
        </Box>)}
      </Stack>
      {preview && <Typography variant="caption" color={preview.allowed ? 'text.secondary' : 'error'} sx={{ display: 'block', mt: 1 }}>Tahmini satır: {preview.estimatedRows.toLocaleString('tr-TR')} / Limit: {preview.maxRows.toLocaleString('tr-TR')}</Typography>}
      {message && <Alert severity={message.includes('hazırlandı') ? 'success' : 'warning'} sx={{ mt: 2 }}>{message}</Alert>}
      <Button sx={{ mt: 2 }} variant="contained" startIcon={<DownloadOutlined />} disabled={loading || busy} onClick={() => void prepareExport()}>Dışa aktar</Button>
    </CardContent>
  </Card>;
};

export default ExportSettings;
